import type { ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}

/**
 * The empty state for the whole app. An empty screen in an ERP is a fact about
 * the ledger, not a design failure: say what is missing and what to do next,
 * in the interface's voice, and keep the primary action present so the user can
 * act without hunting for the button. Never fill it with sample rows.
 *
 * `role="status"` because this block replaces data the user was waiting for —
 * it should be announced once when it arrives, not be silent.
 */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      role="status"
      className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12 text-center', className)}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface-variant text-on-surface-subtle">
        {icon ?? <Inbox className="h-5 w-5" aria-hidden />}
      </div>
      <div className="max-w-md">
        <p className="text-headline-sm text-on-surface">{title}</p>
        {description && <p className="mt-1 text-body-md text-on-surface-variant">{description}</p>}
      </div>
      {action && <div className="mt-1 flex items-center gap-2">{action}</div>}
    </div>
  );
}
